import type { RestangularService } from '../common/restangular.service'

import { app } from '../../app.module'

interface BrandCategory {
  brandCategoryName: string
  subCategoryName: string
  sortNo: number
}

/**
 * ブランドカテゴリフォーム
 * @class BrandCategoryFormCtrl
 */
class BrandCategoryFormCtrl {
  private type: string
  private brandCategories: BrandCategory[]
  private category: string
  private subCategory: string
  private getFromLocalFlg: boolean
  private saveToLocalFlg: boolean
  private includeEmptyOptionFlg: boolean
  private selectedCategory: {
    categoryName: string
    subCategoryName: string
  }

  constructor(
    $scope: ng.IScope,
    private $rootScope,
    private RestangularService: RestangularService,
    private localStorageService
  ) {
    // カテゴリ一覧変更のイベント
    $scope.$on('changeListBrandCategory', () => {
      this.getBrandCategories()
    })
  }

  /**
   * 初期化イベント
   */
  public $onInit() {
    if (this.getFromLocalFlg !== false) {
      this.getFromLocalFlg = true
    }
    if (this.saveToLocalFlg !== false) {
      this.saveToLocalFlg = true
    }
    if (this.includeEmptyOptionFlg !== false) {
      this.includeEmptyOptionFlg = true
    }
    this.brandCategories = []
    this.selectedCategory = {
      categoryName: null,
      subCategoryName: null,
    }
    if (!this.category) {
      this.category = null
    }
    // ブランドカテゴリ一覧の取得
    this.getBrandCategories()
  }

  /**
   * カテゴリの変更
   */
  public changeCategory = () => {
    this.selectedCategory = {
      categoryName: this.category,
      subCategoryName: null,
    }
    if (this.category === '指定しない') {
      this.selectedCategory.categoryName = null
    }
    if (this.saveToLocalFlg) {
      // カテゴリをローカルストレージに保存する
      this.saveCategoryToLocal()
    }
    // ブランドカテゴリ変更イベントをbroadcastする
    this.$rootScope.$broadcast('changeBrandCategory', {
      type: this.type,
      categoryName: this.selectedCategory.categoryName,
      subCategoryName: this.selectedCategory.subCategoryName,
    })
  }

  /**
   * ブランドカテゴリ一覧の取得
   * @private
   */
  private getBrandCategories() {
    const endpoint = '/brand_category/getAllBrandCategory'
    // グループIDとグループの契約プランをクエリに含める
    const params = {
      group_id: this.$rootScope.groupId,
      is_pro: this.$rootScope.isPro,
    }

    // ブランドカテゴリ一覧の取得
    this.RestangularService.request('GET', endpoint, params).then((result) => {
      if (result[0] && result[0].data) {
        this.brandCategories = result[0].data
      }

      // ブランドカテゴリ一覧の取得イベントをbroadcastする
      this.$rootScope.$broadcast('getAllBrandCategory', {
        type: this.type,
        brandCategories: this.brandCategories,
      })

      // ブランド編集ではない場合は、'指定しない'を追加
      if (this.includeEmptyOptionFlg) {
        this.brandCategories.unshift({
          brandCategoryName: '指定しない',
          subCategoryName: '指定しない',
          sortNo: 0,
        })
      }
      // ローカルストレージから選択したカテゴリを取得
      if (this.getFromLocalFlg) {
        this.getCategoryFromLocal()
      }
      // カテゴリをローカルストレージに保存する
      if (this.saveToLocalFlg) {
        this.saveCategoryToLocal()
      }
      // カテゴリがある場合は、セット
      if (this.category) {
        this.selectedCategory = {
          categoryName: this.category,
          subCategoryName: null,
        }
        if (this.subCategory) {
          this.selectedCategory.subCategoryName = this.subCategory
        }
      } else {
        this.category = this.selectedCategory.categoryName
      }
      // ブランドカテゴリ変更イベントをbroadcastする
      this.$rootScope.$broadcast('changeBrandCategory', {
        type: this.type,
        categoryName: this.selectedCategory.categoryName,
        subCategoryName: this.selectedCategory.subCategoryName,
      })
    })
  }

  /**
   * ローカルストレージから選択したカテゴリをセット
   * @private
   */
  private getCategoryFromLocal() {
    // 選択したカテゴリ
    const selectedCategory: {
      categoryName: string
      subCategoryName: string
    } = this.localStorageService.get('selectedCategory')

    // 選択したカテゴリがない場合は、リターン
    if (!selectedCategory) {
      this.selectedCategory = {
        categoryName: null,
        subCategoryName: null,
      }
      return
    }

    this.selectedCategory = selectedCategory

    const filterCategory = this.brandCategories.filter((category) => {
      return category.brandCategoryName === selectedCategory.categoryName
    })[0]

    // ローカルストレージに保存されているブランドカテゴリが存在しない場合
    if (!filterCategory) {
      this.selectedCategory = {
        categoryName: null,
        subCategoryName: null,
      }
      return
    }

    // 選択したサブカテゴリがフィルターカテゴリのサブカテゴリ一覧に存在しない場合は、nullをセット
    if (
      selectedCategory.subCategoryName &&
      (filterCategory.subCategoryName === null ||
        filterCategory.subCategoryName.split(',').indexOf(selectedCategory.subCategoryName) === -1)
    ) {
      this.selectedCategory.subCategoryName = null
    }
  }

  /**
   * カテゴリをローカルストレージに保存する
   * @private
   */
  private saveCategoryToLocal() {
    this.localStorageService.set('selectedCategory', this.selectedCategory)
  }
}

/**
 * ブランドカテゴリフォームコンポーネント
 * attr: {string} type タイプ
 * attr: {string} category カテゴリ
 * attr: {string} subCategory サブカテゴリ
 * attr: {boolean} getFromLocalFlg ローカルストレージから選択したカテゴリをセット
 * attr: {boolean} saveToLocalFlg カテゴリ変更するとき、ローカルストレージに保存するフラグ
 * attr: {boolean} includeEmptyOptionFlg 指定しないを含むフラグ
 */
app.component('brandCategoryForm', {
  bindings: {
    type: '@',
    category: '<',
    subCategory: '<',
    getFromLocalFlg: '<',
    saveToLocalFlg: '<',
    includeEmptyOptionFlg: '<',
  },
  template: require('./brand-category-form.pug')(),
  controller: BrandCategoryFormCtrl,
})
